import { zodResolver } from "@hookform/resolvers/zod";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
} from "@mui/material";
import type { ReactNode } from "react";
import { useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { toast } from "sonner";
import { useSetHandleMutation } from "Api/Mutations/Social/useSetHandleMutation";
import { HandleField } from "Components/Social/HandleField";
import { SocialTermsConsent } from "Components/Social/SocialTermsConsent/SocialTermsConsent";
import { useHandleAvailabilityState } from "Hooks/Social/useHandleAvailabilityState";
import {
  getHandleFormSchema,
  type HandleFormModel,
} from "Schemas/handleSchema";
import { getUserInfoAsync } from "State/Auth/UserInfo/GetUserInfoState";
import { Resources, useResource } from "Translations/Resources";

const RESOURCES = Resources.Chat.HandleGate;
const HANDLE_RESOURCES = Resources.Social.Handle;

type Props = {
  isOpen: boolean;
  /** The gate's only way out (back to the dashboard) — the dialog itself cannot be dismissed. */
  secondaryAction: ReactNode;
};

/**
 * Handle step of the chat onboarding gate (ZS-1320: hard enforcement at the chat feature gate).
 * A persona without a handle picks one right here, inside chat, instead of being sent off to the
 * profile tab. Saving invalidates user-info, and the gate advances to its next step on its own —
 * this dialog has no success callback to wire.
 */
export const ChatHandleGateDialog = ({ isOpen, secondaryAction }: Props) => {
  const { t } = useResource();
  const dispatch = useDispatch();

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<HandleFormModel>({
    // onSubmit mode on purpose (as in the onboarding HandleStep): no red format errors while the
    // user is still typing; empty or taken input just keeps the button disabled.
    // The gate only opens for a persona without a handle, so this is always its FIRST handle —
    // and therefore its consent to the social-features terms (ZS-1749).
    resolver: zodResolver(
      getHandleFormSchema(t, { isSocialTermsConsentRequired: true }),
    ),
    defaultValues: { handle: "", acceptSocialTerms: false },
  });

  const availability = useHandleAvailabilityState(watch("handle"));
  const isEmpty = availability.normalizedInput === "";
  const isSocialTermsConsentMissing = !watch("acceptSocialTerms");

  const { mutateAsync: setHandleAsync, isPending } = useSetHandleMutation({
    onError: (error) => {
      // A 409 on Handle means a concurrent taker won between the live check and the submit.
      toast.error(
        t(
          error?.status === 409 && error.data?.errors?.Handle
            ? HANDLE_RESOURCES.Taken
            : HANDLE_RESOURCES.SaveError,
        ),
      );
    },
  });

  const onSubmit = async (model: HandleFormModel) => {
    try {
      await setHandleAsync(model);
    } catch {
      // Feedback shown by the mutation's onError — stay on the step.
      return;
    }
    // The Redux user carries the handle as well (onboarding steps read it) — keep both in step.
    dispatch(getUserInfoAsync.request(undefined));
  };

  return (
    // The paper itself is the form: title, content and actions stay direct children of the
    // dialog's flex column, so only the content scrolls (the expanded terms summary) and the
    // buttons stay in view. A <form> wrapped around them made the whole dialog scroll instead.
    <Dialog
      open={isOpen}
      disableEscapeKeyDown
      slotProps={{
        paper: { component: "form", onSubmit: handleSubmit(onSubmit) },
      }}
    >
      <DialogTitle>{t(RESOURCES.Title)}</DialogTitle>
      <DialogContent sx={{ pb: 0 }}>
        <DialogContentText mb={3}>{t(RESOURCES.Description)}</DialogContentText>
        <Stack gap={2}>
          <HandleField
            control={control}
            errors={errors}
            name="handle"
            availability={availability}
            autoFocus
          />
          <SocialTermsConsent
            control={control}
            errors={errors}
            name="acceptSocialTerms"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        {secondaryAction}
        <Button
          type="submit"
          variant="contained"
          disabled={
            isEmpty ||
            availability.isChecking ||
            availability.isTaken ||
            isSocialTermsConsentMissing ||
            isPending
          }
        >
          {t(Resources.Social.Terms.AcceptAndContinue)}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
